import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

type Status = Awaited<ReturnType<typeof api.system.updateStatus>>;
type Install = Awaited<ReturnType<typeof api.system.installStatus>>;

/**
 * P1 #53 + #55 — Versions-/Changelog-Übersicht (admin) MIT In-App-Installer.
 * Admin kann ein verfügbares Update per Klick installieren; der Verlauf wird
 * live aus update_runs angezeigt (persistenter Trace, auch später prüfbar).
 */
export function SettingsUpdates() {
  const [info, setInfo] = useState<Status | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [install, setInstall] = useState<Install | null>(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef<number | null>(null);

  const loadInfo = () => api.system.updateStatus().then(setInfo).catch((e) => setErr(e.message));
  const loadInstall = async (): Promise<Install | null> => {
    try { const s = await api.system.installStatus(); setInstall(s); return s; } catch { return null; }
  };

  useEffect(() => {
    loadInfo();
    loadInstall();
    return () => { if (poll.current) window.clearInterval(poll.current); };
  }, []);

  const run = install?.run ?? null;
  const running = !!install?.transition || run?.status === "running" || run?.status === "requested";

  useEffect(() => {
    if (running && poll.current === null) {
      poll.current = window.setInterval(async () => {
        const s = await loadInstall();
        const done = s && !s.transition && s.run && ["success", "failed", "rolled_back"].includes(s.run.status);
        if (done) {
          if (poll.current) { window.clearInterval(poll.current); poll.current = null; }
          setBusy(false);
          // Nach erfolgreichem Update: neues Frontend laden (Banner + Version
          // aktualisieren sich) — ohne manuelles Refresh. Bei Fehler/Rollback
          // bleiben wir auf der Seite, um den Trace + Fehler zu zeigen.
          if (s?.run?.status === "success") {
            window.setTimeout(() => window.location.reload(), 1500);
          } else {
            loadInfo();
          }
        }
      }, 3000);
    }
    if (!running && poll.current !== null) { window.clearInterval(poll.current); poll.current = null; }
  }, [running]);

  const doInstall = async () => {
    if (!info?.updateAvailable) return;
    if (!window.confirm(
      `Update auf ${info.latest} jetzt installieren?\n\n` +
      "• Es wird automatisch ein Backup erstellt.\n" +
      "• Das System startet kurz neu (wenige Sekunden Unterbrechung).\n" +
      "• Bei einem Problem wird automatisch auf die vorherige Version zurückgerollt.\n\nFortfahren?",
    )) return;
    setBusy(true);
    setErr(null);
    try {
      await api.system.install(info.latest);
      await loadInstall();
    } catch (e: any) {
      setBusy(false);
      setErr(e.message);
    }
  };

  const statusColor = (s?: string) =>
    s === "success" ? "#2e9a4f" : s === "running" || s === "requested" ? "#1a6ee8" : "#c13737";

  return (
    <div>
      <div className="kv-card" style={{ marginTop: 14 }}>
        <div className="kv-header"><strong>Programm-Versionen & Änderungen</strong></div>
        {err && <p style={{ color: "#c13737" }}>Fehler: {err}</p>}
        {!info ? (
          <p className="muted">Lade…</p>
        ) : (
          <>
            <p style={{ fontSize: 13, margin: "8px 0" }}>
              Installiert: <strong>{info.current}</strong>
              {info.updateAvailable ? (
                <span style={{ color: "#1a6ee8", marginLeft: 10 }}>— Update verfügbar: <strong>{info.latest}</strong></span>
              ) : (
                <span className="muted" style={{ marginLeft: 10 }}>— Sie sind auf dem neuesten Stand.</span>
              )}
            </p>
            {info.updateAvailable && (
              <button className="primary" onClick={doInstall} disabled={busy || running}>
                {running ? "Update läuft…" : `Update auf ${info.latest} installieren`}
              </button>
            )}
            {info.releases.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 14 }}>
                {info.releases.map((rel) => (
                  <div
                    key={rel.tag}
                    style={{ borderLeft: `3px solid ${rel.tag === info.current ? "#2e9a4f" : "#e2e8f0"}`, paddingLeft: 10 }}
                  >
                    <div style={{ fontSize: 14 }}>
                      <strong>{rel.tag}</strong>
                      {rel.tag === info.current && (
                        <span style={{ color: "#2e9a4f", marginLeft: 8, fontSize: 12 }}>● installiert</span>
                      )}
                      {rel.date && <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>{rel.date}</span>}
                    </div>
                    {rel.changelog && (
                      <div style={{ fontSize: 13, color: "#1a2233", whiteSpace: "pre-wrap", marginTop: 2 }}>{rel.changelog}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {run && (
        <div className="kv-card" style={{ marginTop: 14 }}>
          <div className="kv-header"><strong>Letztes Update — Protokoll</strong></div>
          <p style={{ fontSize: 13, margin: "8px 0" }}>
            {run.from_version} → {run.target_tag} ·{" "}
            <strong style={{ color: statusColor(run.status) }}>{run.status}</strong>
            {run.finished_at && <span className="muted"> · fertig {new Date(run.finished_at).toLocaleString("de-AT")}</span>}
          </p>
          {run.error && <p style={{ color: "#c13737", fontSize: 13 }}>{run.error}</p>}
          <ol style={{ fontSize: 12, color: "#475569", margin: "6px 0 0", paddingLeft: 18 }}>
            {(run.steps ?? []).map((s, i) => (
              <li key={i}>
                <code>{s.step}</code> — {s.detail}{" "}
                <span className="muted">({new Date(s.ts).toLocaleTimeString("de-AT")})</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
